import AxiosInstance from "../../shared/utils/axios";
import {
  appendArrayParams,
  mapProtocolTypeToApiValue,
  mapStatusToApiValue,
  type ProtocolAdvancedFiltersQuery,
} from "./protocolsService";

export interface ProtocolInsightsParams {
  orgId: string;
  workspaceId: string;
  isWorkspace: boolean;
  archived?: boolean;
  search?: string;
  status?: string;
  advancedFilters?: ProtocolAdvancedFiltersQuery | null;
}

export interface InsightsDatum {
  label: string;
  value: number;
}

interface InsightsApiResponse {
  retrieved_at: string;
  data: InsightsDatum[];
}

const buildInsightsParams = ({
  orgId,
  workspaceId,
  isWorkspace,
  archived = false,
  search = "",
  status = "",
  advancedFilters,
}: ProtocolInsightsParams): URLSearchParams => {
  const paramKey = isWorkspace ? "workspace_id" : "org_id";

  const params = new URLSearchParams();

  params.set(paramKey, isWorkspace ? workspaceId : orgId);
  // Per the "Protocol Insights & Detail API Contracts" doc, these two
  // insights endpoints scope by org_id by default — sending workspace_id
  // alone is not enough to switch scope. is_workspace_admin=true must be
  // sent explicitly for the API to scope by workspace_id instead.
  params.set("is_workspace_admin", isWorkspace.toString());
  params.set("archived", archived.toString());

  if (search) {
    params.set("search", search);
  }

  if (status) {
    params.set("status", mapStatusToApiValue(status));
  }

  const mappedAdvancedStatuses =
    advancedFilters?.status?.map(mapStatusToApiValue);
  const mappedAdvancedTypes = advancedFilters?.type?.map(
    mapProtocolTypeToApiValue
  );

  appendArrayParams(params, "status", mappedAdvancedStatuses);
  appendArrayParams(params, "protocol_type", mappedAdvancedTypes);
  appendArrayParams(params, "crop", advancedFilters?.crop);
  appendArrayParams(params, "target", advancedFilters?.target);
  appendArrayParams(params, "country", advancedFilters?.country);
  appendArrayParams(params, "created_by", advancedFilters?.creator);

  return params;
};

const fetchInsights = async (
  path: string,
  params: ProtocolInsightsParams
): Promise<InsightsDatum[]> => {
  const query = buildInsightsParams(params);

  const response = await AxiosInstance.get<InsightsApiResponse>(
    `${path}?${query.toString()}`,
    {
      headers: {
        "org-id": params.orgId,
        "workspace-id": params.workspaceId,
      },
    }
  );

  return response.data.data;
};

/**
 * Fetches protocol counts by status (draft/published) for the protocol
 * status donut chart, scoped by the same filters as `GET /protocols`.
 */
export const fetchProtocolStatusInsights = async (
  params: ProtocolInsightsParams
): Promise<InsightsDatum[]> =>
  await fetchInsights("/protocols/insights/protocols", params);

/**
 * Fetches trial counts by execution status (draft/ready/activated) for
 * trials linked to protocols matching the same filters as `GET /protocols`.
 */
export const fetchTrialExecutionInsights = async (
  params: ProtocolInsightsParams
): Promise<InsightsDatum[]> =>
  await fetchInsights("/protocols/insights/trials", params);
